import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { fetchCompanyVisionBoardUserDetail } from './companyVisionBoardApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  ProgressList,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_COLORS,
  toneChip
} from '../../../../component/common/designSystem';

/**
 * Vision Board → User profile.
 *
 * One person's boards: how many they have made, how much they put on them, how
 * many they set a reminder for, and when they last came back.
 *
 * The board list carries a date, a size, a reminder and a status — and nothing
 * else. No title, no subtitle, no words, no photos, no story, no tags. A vision
 * board is a list of what somebody wants out of their life; that an admin can
 * see they are using the feature is the whole of what belongs on a company
 * dashboard, and the endpoint behind this page never reads the rest.
 *
 * "Empty" is a status in its own right alongside Active and Archived, because
 * an untouched board and a retired one both look like "not in use" and mean
 * opposite things.
 *
 * `fetcher`/`backPath`/`backLabel` are props so the super-admin Vision Board hub
 * can reuse the page against the unscoped endpoint.
 */
const VisionBoardUserDetail = ({
  fetcher = fetchCompanyVisionBoardUserDetail,
  backPath,
  backLabel
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const itemMix = detail?.itemMix || [];
  const topIdeas = detail?.topIdeas || [];
  const boardHistory = detail?.boardHistory || [];

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard icon={STAT_ICONS.catalog} title="Boards" value={user ? user.totalBoards : '-'} />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Things On Them"
          value={user ? user.totalItems : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Items per Board"
          value={user ? user.avgItemsPerBoard : '-'}
        />
        <StatCard
          icon={STAT_ICONS.more}
          title="Empty Boards"
          value={user ? user.emptyBoards : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="With a Reminder"
          value={user ? user.boardsWithReminder : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Last Board"
          value={user?.lastBoardDate || '-'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="Words or Photos" subtitle="How they build a board">
          {user?.totalItems > 0 ? (
            <ProgressList items={itemMix} unit="items" color={CHART_COLORS.brand} />
          ) : (
            <PanelEmpty>Nothing added to a board yet.</PanelEmpty>
          )}
        </Panel>

        {/* Shoorah catalogue copy — the only text on this page. */}
        <Panel title="Ideas They Build From" subtitle="Shoorah prompts they started with">
          {topIdeas.length > 0 ? (
            <ProgressList items={topIdeas} unit="boards" color={CHART_COLORS.pink} />
          ) : (
            <PanelEmpty>No boards started from a Shoorah idea.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Board History" subtitle="First and most recent">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First board made</td>
              <td className="py-2 text-right">{user?.firstBoardDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last board made</td>
              <td className="py-2 text-right">{user?.lastBoardDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Active boards</td>
              <td className="py-2 text-right">{user ? user.activeBoards : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Archived boards</td>
              <td className="py-2 text-right">{user ? user.archivedBoards : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Started from an idea</td>
              <td className="py-2 text-right">{user ? user.boardsFromIdea : '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>

      {/* A date, a size, a reminder and a status. Never a board's name or
          anything on it. */}
      <div className="mt-6">
        <Panel title="Their Boards" subtitle="Most recent first — titles and contents not shown">
          {boardHistory.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Created</th>
                  <th className="py-2 px-4 font-medium text-center">Items</th>
                  <th className="py-2 px-4 font-medium text-center">From an Idea</th>
                  <th className="py-2 px-4 font-medium text-center">Reminder</th>
                  <th className="py-2 font-medium text-right">Status</th>
                </>
              }
            >
              {boardHistory.map((row, index) => (
                <tr key={`${row.createdAt}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.createdAt}</td>
                  <td className="py-2 px-4 text-center">{row.items}</td>
                  <td className="py-2 px-4 text-center">{row.fromIdea ? 'Yes' : '-'}</td>
                  <td className="py-2 px-4 text-center">{row.reminder || '-'}</td>
                  <td className="py-2 text-right">{row.status}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No boards made yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

VisionBoardUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

VisionBoardUserDetail.defaultProps = {
  backLabel: 'Back to Employees'
};

export default VisionBoardUserDetail;
